import { createHashHistory } from "history";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useQueryStateParams } from "./hooks/useQueryStateParams.js";
import { subscribeToDefaultValues } from "./utils/defaultValueSubscribers.js";
import { deleteDefaultValue, getDefaultValue, setDefaultValue } from "./utils/defaultValuesCache.js";
const history = createHashHistory();
function useQueryState(key, typeOrDefaultValueOrOptions, defaultValueOrOptions, opts) {
  const rValueAsString = useRef(null);
  const { value, setValue, info } = useQueryStateParams(typeOrDefaultValueOrOptions, defaultValueOrOptions, opts);
  const rInfo = useRef(info);
  useLayoutEffect(() => void (rInfo.current = info), [info]);
  const { encodeURI: shouldEncodeURI = false } = opts ?? {};
  const handleHistoryChange = useCallback(
    (location) => {
      const params = new URLSearchParams(location.search);
      const paramValue = params.get(key);
      if (paramValue !== void 0 && paramValue !== null) {
        const decodedParamValue = shouldEncodeURI ? window.decodeURI(paramValue) : paramValue;
        if (rValueAsString.current !== decodedParamValue) {
          setValue(rInfo.current.transformer.parse(decodedParamValue));
          rValueAsString.current = decodedParamValue;
        }
      } else {
        rValueAsString.current = paramValue;
        const defaultValue = getDefaultValue(key);
        if (rInfo.current.value !== defaultValue) {
          console.log("Force default value");
          setValue(defaultValue);
        }
      }
    },
    [key, setValue, shouldEncodeURI]
  );
  const clearUrlValue = useCallback(() => {
    const params = new URLSearchParams(history.location.search);
    params.delete(key);
    history.push(
      {
        hash: history.location.hash,
        search: `?${params.toString()}`,
        pathname: history.location.pathname
      },
      history.location.state
    );
  }, [key]);
  const updateUrlValue = useCallback(
    (value2) => {
      if (value2 === void 0) {
        clearUrlValue();
        return;
      }
      const params = new URLSearchParams(history.location.search);
      const v = shouldEncodeURI ? window.encodeURI(rInfo.current.transformer.serialize(value2)) : rInfo.current.transformer.serialize(value2);
      params.set(key, v);
      history.push(
        {
          hash: history.location.hash,
          search: `?${params.toString()}`,
          pathname: history.location.pathname
        },
        history.location.state
      );
    },
    [clearUrlValue, key, shouldEncodeURI]
  );
  useEffect(() => {
    const uid = "";
    const unsubscribeFromDefaultValues = subscribeToDefaultValues({
      key,
      subscriber: () => {
        handleHistoryChange(history.location);
      }
    });
    const unsubscribeFromHistory = history.listen(({ location }) => {
      handleHistoryChange(location);
    });
    if (rInfo.current.defaultValue !== null && rInfo.current.defaultValue !== void 0) {
      setDefaultValue(key, uid, rInfo.current.defaultValue);
    }
    if (rInfo.current.options.initialValue !== void 0 && rInfo.current.options.initialValue !== null) {
      updateUrlValue(rInfo.current.options.initialValue);
    } else {
      handleHistoryChange(history.location);
    }
    return () => {
      unsubscribeFromHistory();
      unsubscribeFromDefaultValues();
      deleteDefaultValue(key, uid);
      if (!rInfo.current.options.persistOnUnmount) {
        clearUrlValue();
      }
    };
  }, [handleHistoryChange, updateUrlValue, clearUrlValue, key]);
  return [value, updateUrlValue, clearUrlValue];
}
export {
  useQueryState
};
//# sourceMappingURL=useQueryState.js.map
